import React, { useEffect } from "react";

import { useLayout } from "@editor/components/shops-ui/DashboardLayout";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { isFeatureEnabled } from "@editor/infra/featureFlags";

import { routes } from "@/editor/utils/router";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import {
  ALargeSmall,
  Code2,
  HeartHandshake,
  Link,
  Puzzle,
  Receipt,
  Settings2,
  Users,
} from "lucide-react";
import { Outlet, useNavigate } from "react-router-dom";

const baseShopSidebarItems = [
  {
    label: "General",
    path: routes.shops.settings.shops.general,
    icon: Settings2,
  },
  {
    label: "Connections",
    path: routes.shops.settings.shops.connections,
    icon: Link,
  },
  {
    label: "Fonts",
    path: routes.shops.settings.shops.fonts,
    icon: ALargeSmall,
  },
];

// NOTE (Berkley, 2025-05-21): Hide Script Manager for V2
const scriptManagerItem = {
  label: "Script Manager",
  path: routes.shops.settings.shops.scripts,
  icon: Code2,
};

const integrationsItem = {
  label: "Integrations",
  path: routes.shops.settings.shops.integrations,
  icon: Puzzle,
};

const ShopSidebarItems = [
  ...baseShopSidebarItems,
  ...(isFeatureEnabled("reaplo") ? [scriptManagerItem] : []),
  integrationsItem,
];

const workspaceSidebarItems = [
  {
    label: "General",
    path: routes.shops.settings.workspace.general,
    icon: Settings2,
  },
  {
    label: "Members",
    path: routes.shops.settings.workspace.members,
    icon: Users,
  },
  {
    label: "Billing",
    path: routes.shops.settings.workspace.billing,
    icon: Receipt,
  },
  {
    label: "Affiliates",
    path: routes.shops.settings.workspace.affiliates,
    icon: HeartHandshake,
  },
];

const isItemSelected = (path: string) => {
  return location.pathname.startsWith(path);
};

const ShopSettings = () => {
  const { setLayout } = useLayout();
  const navigate = useNavigate();
  const projectId = useCurrentProjectId();
  useEffect(() => {
    setLayout({
      title: "Settings",
    });

    return () => {
      setLayout({
        title: undefined,
        description: undefined,
        actions: undefined,
      });
    };
  }, [setLayout]);

  const IconContainer = ({ children }: { children: React.ReactNode }) => (
    <div className="flex h-5 w-5 items-center justify-center rounded-sm bg-surface">
      {children}
    </div>
  );

  return (
    <div className="flex pt-6 w-full overflow-hidden">
      <div className="w-[160px] space-y-2">
        <div className="header-h5">Shop</div>
        <div className="border-b-0.5 border-border pb-2">
          {ShopSidebarItems.map((item) => (
            <MenuItem
              variant="custom"
              startEnhancer={
                <IconContainer>
                  <item.icon size={12} />
                </IconContainer>
              }
              key={item.path}
              onClick={() => {
                if (projectId) {
                  navigate(item.path.replace(":projectId", projectId));
                }
              }}
              UNSAFE_className="text-sm"
              layoutClassName="h-8"
              selected={isItemSelected(
                item.path.replace(":projectId", projectId ?? ""),
              )}
            >
              {item.label}
            </MenuItem>
          ))}
        </div>
        <div className="header-h5">Workspace</div>
        <div>
          {workspaceSidebarItems.map((item) => (
            <MenuItem
              variant="custom"
              startEnhancer={
                <IconContainer>
                  <item.icon size={12} />
                </IconContainer>
              }
              key={item.path}
              onClick={() => {
                if (projectId) {
                  navigate(item.path.replace(":projectId", projectId));
                }
              }}
              UNSAFE_className="text-sm"
              layoutClassName="h-8"
              selected={isItemSelected(
                item.path.replace(":projectId", projectId ?? ""),
              )}
            >
              {item.label}
            </MenuItem>
          ))}
        </div>
      </div>
      <div className="flex-1 px-6 pb-6 overflow-y-auto h-full">
        <Outlet />
      </div>
    </div>
  );
};

export default ShopSettings;
